import { marked, type Token, type Tokens } from 'marked';

/**
 * Markdown → arbre de nœuds, **sans jamais produire de HTML**.
 *
 * Le texte vient d'un agent, qui écrit ce qu'il veut. Passer par du HTML
 * demanderait `dangerouslySetInnerHTML` et un assainisseur, c'est-à-dire une
 * dépendance de plus et une confiance mal placée. On s'arrête donc aux
 * jetons : la console les rend en éléments React, où l'injection n'existe pas.
 *
 * Le découpage bloc / inline reflète celui de Markdown et rend l'ensemble
 * testable sans navigateur — ce que le rendu React ne permettrait pas.
 */

export type Inline =
  | { type: 'texte'; texte: string }
  | { type: 'gras'; enfants: Inline[] }
  | { type: 'italique'; enfants: Inline[] }
  | { type: 'barre'; enfants: Inline[] }
  | { type: 'code'; texte: string }
  | { type: 'lien'; href: string; enfants: Inline[] }
  | { type: 'saut' };

export type Bloc =
  | { type: 'paragraphe'; enfants: Inline[] }
  | { type: 'titre'; niveau: number; enfants: Inline[] }
  | { type: 'code'; langue: string | null; texte: string }
  | { type: 'liste'; ordonnee: boolean; items: Bloc[][] }
  | { type: 'citation'; blocs: Bloc[] }
  | { type: 'tableau'; entetes: Cellule[]; lignes: Cellule[][] }
  | { type: 'separateur' };

/** Une cellule, avec son alignement quand la source en impose un. */
export interface Cellule {
  enfants: Inline[];
  alignement: 'left' | 'center' | 'right' | null;
}

/**
 * Un lien ne peut pointer que vers le web ou un courriel.
 *
 * `javascript:` dans un `href` s'exécute au clic : c'est la seule voie
 * d'injection qui subsiste quand on ne produit pas de HTML. On la ferme ici
 * plutôt que de faire confiance au texte reçu.
 */
export function hrefSur(href: string): string | null {
  const nu = href.trim();
  if (/^(https?:|mailto:)/i.test(nu)) return nu;
  // Un lien relatif ou une ancre reste inoffensif.
  if (/^[#/]/.test(nu)) return nu;
  return null;
}

function inlines(tokens: Token[] | undefined): Inline[] {
  if (!tokens) return [];
  const out: Inline[] = [];

  for (const t of tokens) {
    switch (t.type) {
      case 'text': {
        const sous = (t as Tokens.Text).tokens;
        // Un `text` peut porter ses propres jetons quand il contient du
        // balisage ; sinon c'est du texte nu.
        if (sous && sous.length > 0) out.push(...inlines(sous));
        else out.push({ type: 'texte', texte: (t as Tokens.Text).text });
        break;
      }
      case 'escape':
        out.push({ type: 'texte', texte: (t as Tokens.Escape).text });
        break;
      case 'strong':
        out.push({ type: 'gras', enfants: inlines((t as Tokens.Strong).tokens) });
        break;
      case 'em':
        out.push({ type: 'italique', enfants: inlines((t as Tokens.Em).tokens) });
        break;
      case 'del':
        out.push({ type: 'barre', enfants: inlines((t as Tokens.Del).tokens) });
        break;
      case 'codespan':
        out.push({ type: 'code', texte: (t as Tokens.Codespan).text });
        break;
      case 'br':
        out.push({ type: 'saut' });
        break;
      case 'link': {
        const lien = t as Tokens.Link;
        const href = hrefSur(lien.href);
        const enfants = inlines(lien.tokens);
        // Lien refusé : on garde le texte, on retire seulement la navigation.
        out.push(href ? { type: 'lien', href, enfants } : { type: 'texte', texte: lien.text });
        break;
      }
      case 'image':
        // Pas d'images : la console ne charge rien depuis l'extérieur.
        out.push({ type: 'texte', texte: (t as Tokens.Image).text });
        break;
      default: {
        const brut = (t as { raw?: string }).raw ?? '';
        if (brut) out.push({ type: 'texte', texte: brut });
      }
    }
  }

  return out;
}

function blocs(tokens: Token[]): Bloc[] {
  const out: Bloc[] = [];

  for (const t of tokens) {
    switch (t.type) {
      case 'space':
        break;
      case 'paragraph':
        out.push({ type: 'paragraphe', enfants: inlines((t as Tokens.Paragraph).tokens) });
        break;
      case 'heading': {
        const h = t as Tokens.Heading;
        out.push({ type: 'titre', niveau: Math.min(6, Math.max(1, h.depth)), enfants: inlines(h.tokens) });
        break;
      }
      case 'code': {
        const c = t as Tokens.Code;
        out.push({ type: 'code', langue: c.lang ? c.lang.split(/\s+/)[0]! : null, texte: c.text });
        break;
      }
      case 'list': {
        const l = t as Tokens.List;
        out.push({
          type: 'liste',
          ordonnee: Boolean(l.ordered),
          items: l.items.map((item) => blocs(item.tokens ?? [])),
        });
        break;
      }
      case 'blockquote':
        out.push({ type: 'citation', blocs: blocs((t as Tokens.Blockquote).tokens ?? []) });
        break;
      case 'table': {
        const tab = t as Tokens.Table;
        const cellule = (c: Tokens.TableCell, i: number): Cellule => ({
          enfants: inlines(c.tokens),
          // L'alignement est porté par la colonne, pas par la cellule.
          alignement: tab.align?.[i] ?? null,
        });
        out.push({
          type: 'tableau',
          entetes: tab.header.map(cellule),
          lignes: tab.rows.map((ligne) => ligne.map(cellule)),
        });
        break;
      }
      case 'hr':
        out.push({ type: 'separateur' });
        break;
      case 'text': {
        // Dans un item de liste, le contenu arrive en `text` plutôt qu'en
        // paragraphe : sans ce cas, les listes seraient vides.
        const enfants = inlines((t as Tokens.Text).tokens ?? [{ ...t, type: 'text' } as Token]);
        if (enfants.length > 0) out.push({ type: 'paragraphe', enfants });
        break;
      }
      default: {
        const brut = (t as { raw?: string }).raw?.trim();
        if (brut) out.push({ type: 'paragraphe', enfants: [{ type: 'texte', texte: brut }] });
      }
    }
  }

  return out;
}

export function analyser(markdown: string): Bloc[] {
  if (!markdown.trim()) return [];
  // `gfm` pour les barrés et les listes de tâches, tels que les écrivent les
  // agents. Pas de `breaks` : un retour simple ne doit pas couper une phrase.
  return blocs(marked.lexer(markdown, { gfm: true }));
}
